import { authAPI } from "../services/api";
import useActionLock from "../hooks/useActionLock";
import { useLeaveConfirmation, useReleaseNavigation } from "../hooks/useUnsavedChanges";
import toast from "react-hot-toast";
import {
  FileText,
  Home,
  RefreshCw,
  LogOut,
  Menu,
  Package,
  Receipt,
  Settings,
  CheckSquare,
  Users,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";

import { parametresAPI } from "../services/api";

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: "Tableau de bord", href: "/", icon: Home },
    { name: "Factures", href: "/factures", icon: Receipt },
    { name: "Clients", href: "/clients", icon: Users },
    { name: "Tâches", href: "/taches", icon: CheckSquare },
    { name: "Prestations", href: "/prestations", icon: Package },
    { name: "Devis", href: "/devis", icon: FileText },
    { name: "Markdown en PDF", href: "/markdown-pdf", icon: FileText },
    { name: "Abonnements", href: "/abonnements", icon: RefreshCw },
    { name: "Paramètres", href: "/parametres", icon: Settings },
  ];

  const [warnings, setWarnings] = useState([]);
  const [showBanner, setShowBanner] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktop, setDesktop] = useState(() => typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 768px)").matches);
  const menuTrigger = useRef(null);
  const menuClose = useRef(null);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!mobileMenuOpen || desktop) return;
    menuClose.current?.focus();
    const close = (event) => { if (event.key === "Escape") { setMobileMenuOpen(false); menuTrigger.current?.focus(); } };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [mobileMenuOpen, desktop]);

  const activePage =
    navigation.find((item) => item.href === location.pathname)?.name ||
    "FacturaLite";

  const [loggingOut, runLogout] = useActionLock();
  const prepareLeave = useLeaveConfirmation();
  const releaseNavigation = useReleaseNavigation();
  const handleLogout = () => runLogout(async () => {
    if (!(await prepareLeave())) return;
    try { await authAPI.logout(); }
    catch (failure) {
      if (failure.response?.status !== 401) { toast.error("Impossible de fermer la session sur le serveur. Réessayez."); return; }
    }
    sessionStorage.removeItem("token"); sessionStorage.removeItem("username");
    localStorage.removeItem("token"); localStorage.removeItem("username");
    releaseNavigation();
    navigate("/login", { replace: true });
  });

  useEffect(() => {
    const fetchParametres = async () => {
      try {
        const { data } = await parametresAPI.get();
        const issues = [];
        if (!data?.banque || !String(data.banque).trim())
          issues.push("Nom de la banque");
        if (!data?.banque_adresse || !String(data.banque_adresse).trim())
          issues.push("Adresse de la banque");
        if (!data?.iban || !String(data.iban).trim()) issues.push("IBAN");
        if (!data?.bic || !String(data.bic).trim()) issues.push("BIC");
        if (!data?.delai_paiement || Number(data.delai_paiement) <= 0)
          issues.push("Délai de paiement");
        setWarnings(issues);
      } catch (e) {
        // Si l'appel échoue, prévenir l'utilisateur de configurer ses paramètres
        setWarnings(["Paramètres non configurés"]);
      }
    };
    fetchParametres();
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen md:flex">
      <div
        className={`fixed inset-0 z-40 bg-gray-900/40 transition-opacity duration-200 md:hidden ${
          mobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <aside aria-hidden={!desktop && !mobileMenuOpen} inert={!desktop && !mobileMenuOpen ? "" : undefined}
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-white shadow-xl transition-transform duration-200 md:static md:z-auto md:w-64 md:max-w-none md:translate-x-0 md:shadow-lg ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 md:border-b-0 md:p-6">
          <div className="flex items-center gap-2">
            <Receipt className="h-8 w-8 text-primary-600" />
            <h1 className="text-xl font-bold text-gray-900">FacturaLite</h1>
          </div>
          <button
            type="button"
            ref={menuClose} onClick={() => { setMobileMenuOpen(false); menuTrigger.current?.focus(); }}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 md:hidden"
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 md:mt-4">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || (item.href === "/factures" && location.pathname.startsWith("/factures/"));
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-5 py-3 text-sm font-medium transition-colors duration-200 md:px-6 ${
                  isActive
                    ? "border-r-2 border-primary-600 bg-primary-50 text-primary-700"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <button
            disabled={loggingOut} onClick={handleLogout}
            className="flex w-full items-center rounded px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-200 hover:bg-gray-50 hover:text-red-600"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Déconnexion
          </button>
          <div className="mt-2 px-4 text-xs text-gray-500">
            Connecté : {sessionStorage.getItem("username") || "admin"}
          </div>
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              ref={menuTrigger} onClick={() => setMobileMenuOpen(true)}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">
                FacturaLite
              </p>
              <p className="truncate text-xs text-gray-500">{activePage}</p>
            </div>
          </div>
        </header>

        {/* Warning banner for missing critical settings */}
        {showBanner && warnings.length > 0 && (
          <div className="px-4 pt-4 sm:px-6 lg:px-8">
            <div className="rounded border-l-4 border-yellow-500 bg-yellow-50 p-4 text-yellow-800">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="font-semibold">Configuration requise</p>
                  <p className="text-sm mt-1">
                    Veuillez compléter les champs suivants dans les Paramètres :
                    <span className="font-medium"> {warnings.join(", ")}</span>.
                    <span className="ml-2">
                      <Link to="/parametres" className="underline">
                        Ouvrir la page Paramètres
                      </Link>
                    </span>
                  </p>
                </div>
                <button
                  aria-label="Fermer l'alerte"
                  className="self-start text-yellow-700 hover:text-yellow-900"
                  onClick={() => setShowBanner(false)}
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
